import { useEffect, useMemo, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useUpdateMasterCapabilitiesMutation, useUpdateMasterParametersMutation } from '../api/master.api';
import {
  FEATURE_FLAG_KEYS,
  MasterParametersPatch,
  ProjectCapabilities,
  ProjectDetail,
  ProjectFeatureFlags,
} from '../api/types';
import MasterCapabilitiesSection from './MasterCapabilitiesSection';
import MasterIdentityCard from './MasterIdentityCard';
import MasterParametersSection from './MasterParametersSection';
import MasterPhoneCard from './MasterPhoneCard';
import MasterToolsSection from './MasterToolsSection';

interface Props {
  project: ProjectDetail;
}

const DEFAULT_FINANCIAL_WEIGHT = 0.6;
const DEFAULT_AI_WEIGHT = 0.2;
const DEFAULT_DOWN_PAYMENT = 0.2;

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const { data } = error as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return fallback;
};

const buildFlags = (project: ProjectDetail): ProjectFeatureFlags =>
  FEATURE_FLAG_KEYS.reduce((acc, key) => {
    acc[key] = project[key];
    return acc;
  }, {} as ProjectFeatureFlags);

const buildCapabilities = (project: ProjectDetail): ProjectCapabilities => ({
  ...buildFlags(project),
  assignmentAgentEnabled: project.assignmentAgentEnabled ?? false,
});

const MasterConfigSections = ({ project }: Props) => {
  const [saveCapabilities, capabilitiesState] = useUpdateMasterCapabilitiesMutation();
  const [saveParameters, parametersState] = useUpdateMasterParametersMutation();

  const [capabilities, setCapabilities] = useState<ProjectCapabilities>(() => buildCapabilities(project));
  const [financialScoreWeight, setFinancialScoreWeight] = useState(
    project.financialScoreWeight ?? DEFAULT_FINANCIAL_WEIGHT
  );
  const [aiScoreWeight, setAiScoreWeight] = useState(project.aiScoreWeight ?? DEFAULT_AI_WEIGHT);
  const [minimumDownPaymentRate, setMinimumDownPaymentRate] = useState(
    project.minimumDownPaymentRate ?? DEFAULT_DOWN_PAYMENT
  );

  const [capabilitiesError, setCapabilitiesError] = useState<string | null>(null);
  const [parametersError, setParametersError] = useState<string | null>(null);

  useEffect(() => {
    setCapabilities(buildCapabilities(project));
    setFinancialScoreWeight(project.financialScoreWeight ?? DEFAULT_FINANCIAL_WEIGHT);
    setAiScoreWeight(project.aiScoreWeight ?? DEFAULT_AI_WEIGHT);
    setMinimumDownPaymentRate(project.minimumDownPaymentRate ?? DEFAULT_DOWN_PAYMENT);
  }, [project]);

  const capabilitiesPatch = useMemo<Partial<ProjectCapabilities>>(() => {
    const next: Partial<ProjectCapabilities> = {};
    const original = buildCapabilities(project);

    FEATURE_FLAG_KEYS.forEach((key) => {
      if (capabilities[key] !== original[key]) next[key] = capabilities[key];
    });

    if (capabilities.assignmentAgentEnabled !== original.assignmentAgentEnabled) {
      next.assignmentAgentEnabled = capabilities.assignmentAgentEnabled;
    }

    return next;
  }, [capabilities, project]);

  const isCapabilitiesDirty = Object.keys(capabilitiesPatch).length > 0;

  const parametersPatch = useMemo<MasterParametersPatch>(() => {
    const next: MasterParametersPatch = {};

    if (financialScoreWeight !== (project.financialScoreWeight ?? DEFAULT_FINANCIAL_WEIGHT)) {
      next.financialScoreWeight = financialScoreWeight;
    }
    if (aiScoreWeight !== (project.aiScoreWeight ?? DEFAULT_AI_WEIGHT)) {
      next.aiScoreWeight = aiScoreWeight;
    }
    if (minimumDownPaymentRate !== (project.minimumDownPaymentRate ?? DEFAULT_DOWN_PAYMENT)) {
      next.minimumDownPaymentRate = minimumDownPaymentRate;
    }

    return next;
  }, [aiScoreWeight, financialScoreWeight, minimumDownPaymentRate, project]);

  const isParametersDirty = Object.keys(parametersPatch).length > 0;
  const scoreTotal = financialScoreWeight + aiScoreWeight;
  const isScoreTotalValid = scoreTotal <= 1;

  const handleCapabilitiesSave = async () => {
    if (!isCapabilitiesDirty) return;
    setCapabilitiesError(null);

    try {
      await saveCapabilities({ id: project.id, patch: capabilitiesPatch }).unwrap();
    } catch (error) {
      setCapabilitiesError(extractErrorMessage(error, 'No se pudo guardar las capacidades.'));
    }
  };

  const handleParametersCancel = () => {
    setFinancialScoreWeight(project.financialScoreWeight ?? DEFAULT_FINANCIAL_WEIGHT);
    setAiScoreWeight(project.aiScoreWeight ?? DEFAULT_AI_WEIGHT);
    setMinimumDownPaymentRate(project.minimumDownPaymentRate ?? DEFAULT_DOWN_PAYMENT);
  };

  const handleParametersSave = async () => {
    if (!isParametersDirty || !isScoreTotalValid) return;
    setParametersError(null);

    try {
      await saveParameters({
        id: project.id,
        patch: parametersPatch,
      }).unwrap();
    } catch (error) {
      setParametersError(extractErrorMessage(error, 'No se pudo guardar los parámetros.'));
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-display text-lg font-semibold">
          Configuración General - {project.name} <span className="text-muted-foreground">({project.id})</span>
        </h2>
      </header>

      <MasterIdentityCard project={project} />

      <MasterPhoneCard project={project} />

      <Tabs defaultValue="features">
        <TabsList>
          <TabsTrigger value="features">Funcionalidades</TabsTrigger>
          <TabsTrigger value="conditions">Condiciones de venta</TabsTrigger>
          <TabsTrigger value="tools">Herramientas</TabsTrigger>
        </TabsList>

        <TabsContent value="conditions" className="mt-4">
          <div className="rounded-md border border-border p-6">
            <MasterParametersSection
              aiScoreWeight={aiScoreWeight}
              error={parametersError}
              financialScoreWeight={financialScoreWeight}
              isDirty={isParametersDirty}
              isLoading={parametersState.isLoading}
              isScoreTotalValid={isScoreTotalValid}
              minimumDownPaymentRate={minimumDownPaymentRate}
              scoreTotal={scoreTotal}
              onAiScoreWeightChange={setAiScoreWeight}
              onCancel={handleParametersCancel}
              onFinancialScoreWeightChange={setFinancialScoreWeight}
              onSave={handleParametersSave}
            />
          </div>
        </TabsContent>

        <TabsContent value="features" className="mt-4">
          <MasterCapabilitiesSection
            capabilities={capabilities}
            error={capabilitiesError}
            hasFunnel={project.hasFunnel}
            isDirty={isCapabilitiesDirty}
            isLoading={capabilitiesState.isLoading}
            projectId={project.id}
            onCapabilitiesChange={setCapabilities}
            onSave={handleCapabilitiesSave}
          />
        </TabsContent>

        <TabsContent value="tools" className="mt-4">
          <MasterToolsSection projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MasterConfigSections;
